import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { panelStyle } from "../ui/card-frame";
import { onAnalyticsConsent, readAnalyticsConsent } from "../consent/analytics-consent";
import { track } from "../analytics/posthog";
import { storeForUserAgent, type AppStore } from "../../lib/app-store";
import {
  APP_PROMO_DELAY_MS,
  countPageView,
  isAppPromoExcludedPath,
  isAppPromoSnoozed,
  isInAppWebView,
  isModalOpen,
  isSecondPageView,
  readClosedAt,
  shouldShowAppPromo,
  writeClosedAt,
} from "./app-promo-rules";
import { APP_MUTED, APP_NAVY, AppIcon, AppQr, StoreBadges } from "./app-promo-parts";
import { BEVEL_INSET_NAVY, BEVEL_NAVY_FACE } from "@majourneys/shared/ui";

type Surface = "popup" | "card";

const SHOWN_KEY = "mj_app_promo_shown_";

/**
 * Every rule in app-promo-rules.ts, wired to the page. Client only: nothing
 * renders on the server or on the first paint, so the edge-cached HTML is the
 * same for everyone and there is no hydration gap.
 */
export function useAppPromoVisible(pathname: string): { visible: boolean; close: () => void } {
  const [armed, setArmed] = useState(false);
  const [snoozed, setSnoozed] = useState(true);
  const [consentAnswered, setConsentAnswered] = useState(false);
  const [webView, setWebView] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setSnoozed(isAppPromoSnoozed(readClosedAt(), Date.now()));
    setWebView(isInAppWebView(window as unknown as Parameters<typeof isInAppWebView>[0]));
    // The cookie banner owns the guest's attention until it is answered.
    setConsentAnswered(readAnalyticsConsent() !== null);
    const off = onAnalyticsConsent(() => setConsentAnswered(true));
    const timer = window.setTimeout(() => setArmed(true), APP_PROMO_DELAY_MS);
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);

  // One count per page. The ref keeps StrictMode's double effect from
  // counting the first page twice.
  const counted = useRef<string | null>(null);
  useEffect(() => {
    if (counted.current === pathname) return;
    counted.current = pathname;
    if (isSecondPageView(countPageView())) setArmed(true);
  }, [pathname]);

  // A sheet or modal can open at any time; check once a second while the
  // promo would otherwise be up, and step aside until it closes.
  const eligible = armed && !snoozed && consentAnswered && !webView && !isAppPromoExcludedPath(pathname);
  useEffect(() => {
    if (!eligible) return;
    const check = () => setModalOpen(isModalOpen(document));
    check();
    const id = window.setInterval(check, 1000);
    return () => window.clearInterval(id);
  }, [eligible]);

  const close = useCallback(() => {
    writeClosedAt(Date.now());
    setSnoozed(true);
  }, []);

  return {
    visible: shouldShowAppPromo({ armed, pathname, snoozed, consentAnswered, inAppWebView: webView, modalOpen }),
    close,
  };
}

function useMinWidth(px: number): boolean | null {
  const [wide, setWide] = useState<boolean | null>(null);
  useEffect(() => {
    const mq = window.matchMedia?.(`(min-width: ${px}px)`);
    if (!mq) return setWide(true);
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [px]);
  return wide;
}

/**
 * "Book on the app": a small card bottom-right on computers and tablets, a
 * floating pewter card on phones (owner, 2026-10-17, app-prompt-lab "M":
 * it replaced the slim strip under the header). Mounted once in the `$lang`
 * layout, so the 20-second clock runs across pages.
 *
 * `headerless`: activity and hotel pages. On a phone their sticky Book bar
 * sits where the card floats, and those are the pages that earn bookings, so
 * the card waits for the next page there.
 */
export function AppPromo({ headerless = false }: { headerless?: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { visible, close } = useAppPromoVisible(pathname);
  const wide = useMinWidth(768);
  // From 1024px the header carries "Get the app" (AppHeaderButton), so the
  // floating card stays away: it sat on the gallery's own buttons (lab round
  // 26, item 199). Tablets, whose header has no room for it, keep the card.
  const desktop = useMinWidth(1024);
  const surface: Surface | null =
    wide === null || desktop === null ? null : desktop ? null : wide ? "popup" : headerless ? null : "card";
  const show = visible && surface !== null;

  useEffect(() => {
    if (!show || !surface) return;
    try {
      if (sessionStorage.getItem(SHOWN_KEY + surface)) return;
      sessionStorage.setItem(SHOWN_KEY + surface, "1");
    } catch {
      // Storage blocked: report it anyway, once per mount.
    }
    track("app_promo_shown", { surface });
  }, [show, surface]);

  if (!show || !surface) return null;

  const onClose = () => {
    track("app_promo_closed", { surface });
    close();
  };
  // Going to the store is an answer too: no need to ask again for 14 days.
  // Closed a beat later: a link removed from the page during its own click
  // is no longer connected, and the browser drops the navigation.
  const onStore = (store: AppStore | "other") => {
    track("app_promo_store_click", { surface, store });
    window.setTimeout(close, 400);
  };

  return surface === "popup" ? <AppPromoPopup onClose={onClose} onStore={onStore} /> : <AppPromoCard onClose={onClose} onStore={onStore} />;
}

interface PromoProps {
  onClose: () => void;
  onStore: (store: AppStore | "other") => void;
}

function CloseButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={t("app_promo.close")}
      className="mj-press grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors hover:bg-[rgba(17,14,57,0.09)]"
      style={{ color: APP_MUTED, background: "rgba(17,14,57,0.05)" }}
    >
      <Icon name="ri-close-line" className="text-[17px]" aria-hidden="true" />
    </button>
  );
}

/** Computers and tablets: the white pewter card, QR and both store badges. */
export function AppPromoPopup({ onClose, onStore }: PromoProps) {
  const { t } = useTranslation();
  return (
    <div
      role="dialog"
      aria-label={t("app_promo.dialog_label")}
      className="mj-app-promo-pop mj-rise-in fixed end-4 z-[100] w-[340px] p-4 lg:end-6"
      style={{ ...panelStyle(24), boxShadow: `${panelStyle(24).boxShadow}, 0 18px 40px -18px rgba(17,14,57,0.35)` }}
    >
      <div className="flex items-start gap-3">
        <AppIcon size={46} />
        <div className="min-w-0 flex-1">
          <p className="text-[15.5px] font-semibold leading-snug" style={{ color: APP_NAVY }}>
            {t("app_promo.popup_title")}
          </p>
          <p className="mt-0.5 text-balance text-[13px] font-medium leading-snug" style={{ color: APP_MUTED }}>
            {t("app_promo.popup_sub")}
          </p>
        </div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="mt-3 flex items-center gap-3 rounded-[16px] p-2.5" style={{ background: "rgba(17,14,57,0.04)" }}>
        <AppQr size={78} />
        <div className="min-w-0">
          <p className="text-[12.5px] font-semibold" style={{ color: APP_NAVY }}>
            {t("app_promo.scan")}
          </p>
          <div className="mt-1.5">
            <StoreBadges height={36} onPick={onStore} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The four reasons on the phone card (owner, 2026-10-17). */
const REASONS = [
  { icon: "ri-calendar-check-line", key: "bookings" },
  { icon: "ri-notification-3-line", key: "updates" },
  { icon: "ri-sparkling-2-line", key: "ai" },
  { icon: "ri-map-2-line", key: "planner" },
] as const;

/** The pressed-in round that holds each reason's icon and the close X. */
const WELL = {
  background: "#E9E9EF",
  boxShadow: "inset 0 2px 4px rgba(17,14,57,0.13), inset 0 -1px 0 #fff",
} as const;

/**
 * Phones: a floating pewter card above a dimmed page. The app's Home in a
 * small phone, the four reasons, one button to this phone's store through
 * `/app`, and "Not now".
 *
 * Not `aria-modal`: `isModalOpen` polls for that attribute to step aside for
 * the site's own sheets, and the card would find itself and hide.
 */
export function AppPromoCard({ onClose, onStore }: PromoProps) {
  const { t } = useTranslation();
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const store = typeof navigator === "undefined" ? null : storeForUserAgent(navigator.userAgent);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    // Above the header (--z-header 999) so the whole page dims, below the
    // site's own modals (--z-modal 9999).
    <div className="fixed inset-0 z-[1000]">
      {/* Tapping the dimmed page is a close like the X: same 14 days. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className="mj-fade-in absolute inset-0 h-full w-full cursor-default bg-[rgba(17,14,57,0.34)]"
      />
      <div
        role="dialog"
        aria-label={t("app_promo.dialog_label")}
        className="mj-app-promo-card mj-rise-in absolute inset-x-2.5 mx-auto max-w-[460px] px-5 pb-5 pt-5"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          ...panelStyle(30),
          boxShadow: `${panelStyle(30).boxShadow}, 0 24px 48px -18px rgba(17,14,57,0.45), 0 6px 14px -6px rgba(17,14,57,0.18)`,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("app_promo.close")}
          className="mj-press absolute end-3.5 top-3.5 grid h-9 w-9 place-items-center rounded-full"
          style={{ ...WELL, color: APP_NAVY }}
        >
          <Icon name="ri-close-line" className="text-[18px]" aria-hidden="true" />
        </button>

        <div className="mb-4 flex items-center gap-3 pe-11">
          <AppIcon size={44} />
          <div className="min-w-0">
            <p className="text-balance text-[18px] font-bold leading-tight" style={{ color: APP_NAVY }}>
              {t("app_promo.card_title")}
            </p>
            <p className="text-[12.5px] font-medium" style={{ color: APP_MUTED }}>
              {t("app_promo.card_sub")}
            </p>
          </div>
        </div>

        <div className="mb-5 flex items-center gap-4">
          <div
            className="relative shrink-0 overflow-hidden rounded-[20px] border-[4px] border-[#16151d] bg-[#F2F2F6]"
            style={{ width: 96, height: 196, boxShadow: "0 14px 26px -12px rgba(17,14,57,0.45)" }}
          >
            <img
              src="/images/app/home.webp"
              alt={t("app_promo.section_image_alt")}
              width={600}
              height={1305}
              decoding="async"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <ul className="flex min-w-0 flex-1 flex-col gap-3">
            {REASONS.map((r) => (
              <li key={r.key} className="flex items-center gap-2.5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full" style={{ ...WELL, color: APP_NAVY }}>
                  <Icon name={r.icon} className="text-[17px]" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-bold leading-tight" style={{ color: APP_NAVY }}>
                    {t(`app_promo.reason_${r.key}_title`)}
                  </span>
                  <span className="block text-balance text-[11.5px] font-medium leading-snug" style={{ color: APP_MUTED }}>
                    {t(`app_promo.reason_${r.key}_line`)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`/${lang ?? "en"}/app`}
            onClick={() => onStore(store ?? "other")}
            aria-label={t("app_promo.open_label")}
            className="mj-press flex min-h-12 flex-1 items-center justify-center rounded-full px-4 text-center text-[15px] font-semibold text-white"
            style={{
              background: BEVEL_NAVY_FACE,
              boxShadow: `0 12px 22px -10px rgba(17,14,57,0.6), ${BEVEL_INSET_NAVY}`,
            }}
          >
            {t("app_promo.use_app")}
          </a>
          <button
            type="button"
            onClick={onClose}
            className="mj-press min-h-12 shrink-0 rounded-full px-4 text-[14px] font-semibold"
            style={{ color: APP_MUTED }}
          >
            {t("app_promo.not_now")}
          </button>
        </div>
      </div>
    </div>
  );
}
